<template>
    <Tabs />

    <DCard padding>
        <Search
            v-model:search="filter.search.value"
            v-model:date-from="filter.dateFrom.value"
            v-model:date-to="filter.dateTo.value"
            @filter="applyFilter"
        />
        <hr>
        <div class="bankruptcy-monitor-history-table">
            <header>
                <div>{{ __('tools.bankruptcy_monitor.history.headers.file') }}</div>
                <div>{{ __('tools.bankruptcy_monitor.history.headers.date') }}</div>
                <div>{{ __('tools.bankruptcy_monitor.history.headers.invoice_id') }}</div>
                <div>{{ __('tools.bankruptcy_monitor.history.headers.customer_id') }}</div>
                <div>{{ __('tools.bankruptcy_monitor.history.headers.company_name') }}</div>
                <div>{{ __('tools.bankruptcy_monitor.history.headers.customer_firstname') }}</div>
                <div>{{ __('tools.bankruptcy_monitor.history.headers.customer_lastname') }}</div>
                <div>{{ __('tools.bankruptcy_monitor.history.headers.invoice_amount') }}</div>
                <div>{{ __('tools.bankruptcy_monitor.history.headers.actions') }}</div>
            </header>
            <main>
                <div
                    v-for="dataBM in bankruptcyMonitor.data"
                    :key="dataBM.id"
                    class="item-group"
                >
                    <div class="item-header">
                        <div>
                            <DButton
                                mini
                                :icon="!collapsedGroups.includes(dataBM.id) ? 'arrow-down' : 'arrow-up'"
                                @click="toggleGroup(dataBM.id)"
                            />
                        </div>
                        <div>{{ dataBM.file }}</div>
                        <div>{{ dataBM.date }}</div>
                        <div />
                        <div />
                        <div />
                        <div />
                        <div />
                        <div />
                        <div>
                            <DButton
                                icon="download"
                                mini
                                :label="__('tools.bankruptcy_monitor.history.actions.download_csv')"
                                @click="downloadCsv(dataBM)"
                            />
                            <DButton
                                icon="download"
                                mini
                                :label="__('tools.bankruptcy_monitor.history.actions.download_zip')"
                                @click="downloadZip(dataBM)"
                            />
                        </div>
                    </div>
                    <div
                        v-for="invoice in dataBM.invoices"
                        v-show="collapsedGroups.includes(dataBM.id)"
                        :key="invoice.id"
                        class="item"
                    >
                        <div />
                        <div />
                        <div>{{ invoice.date }}</div>
                        <div v-if="userPermissions.includes('invoice.view')">
                            <Link :href="showInvoiceRoute(invoice)">
                                {{ invoice.id }}
                            </Link>
                        </div>
                        <div v-else>
                            {{ invoice.id }}
                        </div>
                        <div v-if="userPermissions.includes('customer.view')">
                            <Link :href="customerInvoicesRoute(invoice)">
                                {{ invoice.customer.id }}
                            </Link>
                        </div>
                        <div v-else>
                            {{ invoice.customer.id }}
                        </div>
                        <div>{{ invoice.customer.company }}</div>
                        <div>{{ invoice.customer.firstname }}</div>
                        <div>{{ invoice.customer.lastname }}</div>
                        <div>{{ priceFormat.priceFormatForDisplay(invoice.total_amount, 2, invoice.customer.market_region_id) }}</div>
                    </div>
                </div>
            </main>
        </div>
        <DPagination
            v-if="bankruptcyMonitor.last_page > 1"
            class="pb-2"
            :active-page="bankruptcyMonitor.current_page"
            :pages="bankruptcyMonitor.last_page"
            align="center"
            @update:activePage="changePage"
        />
    </DCard>
</template>

<script setup>
import { ref, computed } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import Search from '~@/Components/Tools/Collection/Search.vue'
import Tabs from '~@/Components/BillingAndAccounting/BankruptcyMonitor/Tabs.vue'
import priceFormat from '~@/Composables/priceFormat'

const props = defineProps({
    bankruptcyMonitor: {
        type: Object,
        default: () => ({})
    },
    appliedFilters: {
        type: Object,
        default: () => ({})
    }
})

const collapsedGroups = ref([])
const filter = {
    search: ref(props.appliedFilters.search),
    dateFrom: ref(props.appliedFilters.dateFrom),
    dateTo: ref(props.appliedFilters.dateTo)
}

const userPermissions = computed(() => usePage().props.auth.permissions)

const showInvoiceRoute = (invoice) => {
    return route('customers.customers.invoices.show', {
        customer: invoice.customer.id,
        invoice: invoice.id
    })
}

const customerInvoicesRoute = (invoice) => {
    return route('customers.customers.invoices.index', { customer: invoice.customer.id })
}

const changePage = (page) => {
    const queryParameters = Object.fromEntries(Object.entries(filter).filter(([_, v]) => v.value))

    if (page !== 1) {
        queryParameters.page = page
    }

    router.get(route('tools.bankruptcy-monitor.history'), queryParameters)
}

const downloadCsv = (item) => {
    const link = document.createElement('a')
    link.href = route('tools.bankruptcy-monitor.download-csv', { bankruptcy_monitor_export: item.id })
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

const downloadZip = (item) => {
    const link = document.createElement('a')
    link.href = route('tools.bankruptcy-monitor.download-zip', { bankruptcy_monitor_export: item.id })
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

const toggleGroup = (index) => {
    if (collapsedGroups.value.includes(index)) {
        collapsedGroups.value = collapsedGroups.value.filter(e => e !== index)
    } else {
        collapsedGroups.value.push(index)
    }
}

const applyFilter = () => {
    const queryParameters = Object.fromEntries(
        Object.entries(filter)
            .filter(([_, v]) => v.value !== undefined && v.value !== null && v.value !== '')
            .map(([key, v]) => [key, v.value])
    )

    router.get(route('tools.bankruptcy-monitor.history'), queryParameters)
}
</script>

<style lang="scss" scoped>
$tablecolumns: 50px 1fr 100px 150px 120px 1fr 1fr 1fr 185px 315px;

.bankruptcy-monitor-history-table .exclude-checkbox {
    margin-bottom: 23px;
}

.bankruptcy-monitor-history-table header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.14);
    display: grid;
    grid-template-columns: $tablecolumns;
    grid-gap: 10px;
}

.bankruptcy-monitor-history-table header > div:nth-child(1) {
    grid-column: 2;
}

.bankruptcy-monitor-history-table header > div {
    padding: 10px;
    font-weight: bold;
}

.bankruptcy-monitor-history-table main {
}

.bankruptcy-monitor-history-table main .item-group {
    border-bottom: 1px solid rgba(0, 0, 0, 0.3);
    position: relative;
}

.bankruptcy-monitor-history-table main .item-group .item-header {
    display: grid;
    grid-template-columns: $tablecolumns;
    grid-gap: 10px;
    background: #fff;
    border-bottom: 1px solid rgba(0, 0, 0, 0.14);
    position: sticky;
    top: -25px;
    width: 100%;
    z-index: 100;
}

.bankruptcy-monitor-history-table main .item-group .item-header > div {
    padding: 10px;
    align-self: center;
}

.bankruptcy-monitor-history-table main .item-group .item {
    display: grid;
    grid-template-columns: $tablecolumns;
    grid-gap: 10px;
}

.bankruptcy-monitor-history-table main .item-group .item > div {
    padding: 10px;
    align-self: center;
}

.bankruptcy-monitor-history-table main .item-group .item > div:nth-child(1) {
    position: relative;
    overflow: hidden;
    padding: 0;
    height: 100%;
}

.bankruptcy-monitor-history-table main .item-group .item > div:nth-child(1)::after {
    content: "";
    display: block;
    position: absolute;
    left: 21px;
    top: 0;
    width: 2px;
    height: 60px;
    background: rgba(0, 0, 0, 0.07);
}

.bankruptcy-monitor-history-table main .item-group .item > div:nth-child(1)::before {
    content: "";
    display: block;
    position: absolute;
    left: 23px;
    top: 25px;
    width: 10px;
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
    height: 2px;
    background: rgba(0, 0, 0, 0.07);
}

.bankruptcy-monitor-history-table main .item-group .item:last-of-type > div:nth-child(1)::after {
    height: 35px;
    background: rgba(0, 0, 0, 0.07);
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
}

.bankruptcy-monitor-history-table main .item-group .item:hover {
    background: rgba(0, 0, 0, 0.07);
}
</style>
